import { type TaskQueue, type TaskQueueState, useTaskQueueWithAtom } from "$root/utils";
import { type Atom, readAtom } from "@mdotm/shadcn/legacy/reactivity";
import { useMandatoryContext } from "@mdotm/utils/react-extra";
import { type ReactNode, createContext, useState } from "react";
import { createSharedConceptService } from "../services/sharedConceptService";
import type { WorkspaceAction } from "./action";
import { selectSharedConcepts } from "./selectors";
import { createWorkspaceSideEffects } from "./side-effects";
import { type WorkspaceSlices, createWorkspaceSlices } from "./slices";
import type { WorkspaceState } from "./state";
import { type WorkspaceStore, createWorkspaceStore } from "./store";

/**
 * What the area is given in place of the ten atoms and the five controllers: one `dispatch`, one set of
 * read-only slices, and the queue that owns the "saving / saved at" affordance.
 *
 * There is no `state$` here on purpose. A component that could read the whole value would re-render on every
 * action; a component that could *write* it would be the nineteenth bypassing write. Reads go through
 * {@link WorkspaceSlices}, and the one imperative read — `readState`, for event handlers that need the
 * current value rather than a subscription — is deliberately not a hook and not reactive.
 */
export type WorkspaceStoreContextProps = {
	dispatch: WorkspaceStore["dispatch"];
	slices: WorkspaceSlices;
	/** The current value, for handlers. Not a subscription — see the note above. */
	readState(): WorkspaceState;
	updateQueueState$: Atom<TaskQueueState>;
	updateQueue: TaskQueue;
	/** Re-sends the document, for the Retry the toolbar indicator offers on a failed save. */
	retrySave: WorkspaceStore["persist"];
};

export const WorkspaceStoreContext = createContext<WorkspaceStoreContextProps | null>(null);

/**
 * Creates the store for one workspace document.
 *
 * `initialState` is read once, on mount: the page keys the whole subtree on the workspace uuid, so a
 * different workspace is a different store rather than a store that gets reassigned underneath its
 * subscribers. It has to arrive **repaired** (`ingestWorkspace`), because from here on an invariant
 * violation throws.
 */
export function WorkspaceStoreProvider(props: { initialState: WorkspaceState; children: ReactNode }) {
	const [updateQueueState$, updateQueue] = useTaskQueueWithAtom();

	const value = useState<WorkspaceStoreContextProps>(() => {
		// The service needs to read and publish the pool, and the store needs the service: the knot is tied
		// with a `let` rather than by giving either of them the other's job.
		let store: WorkspaceStore | undefined;
		const readState = () => readAtom(store!.state$);

		const sharedConcepts = createSharedConceptService({
			taskQueue: updateQueue,
			find: (uuid) => selectSharedConcepts(readState()).find((concept) => concept.uuid === uuid),
			publish: (concepts) => {
				void store!.dispatch({ type: "sharedConceptsReplaced", concepts });
			},
		});

		store = createWorkspaceStore({
			initialState: props.initialState,
			taskQueue: updateQueue,
			sideEffects: createWorkspaceSideEffects(sharedConcepts),
		});

		return {
			dispatch: store.dispatch,
			slices: createWorkspaceSlices(store.state$),
			readState,
			updateQueueState$,
			updateQueue,
			retrySave: store.persist,
		};
	})[0];

	return <WorkspaceStoreContext.Provider value={value}>{props.children}</WorkspaceStoreContext.Provider>;
}

export function useWorkspaceStore(): WorkspaceStoreContextProps {
	return useMandatoryContext(WorkspaceStoreContext);
}

/** The one way to change a workspace. */
export function useWorkspaceDispatch(): (action: WorkspaceAction) => Promise<void> {
	return useMandatoryContext(WorkspaceStoreContext).dispatch;
}

/** The read-only slices to subscribe to. Stable for the life of the store. */
export function useWorkspaceSlices(): WorkspaceSlices {
	return useMandatoryContext(WorkspaceStoreContext).slices;
}
